import { Array as A, Option, pipe } from 'effect';

import { MARKED_ATTRIBUTES } from '@/lib/i18nOverrides/domScan';
import { MARKER_START, markerIndexes } from '@/lib/i18nOverrides/markers';
import { intersectRects, isEmptyRect, unionRects, type Rect } from '@/lib/i18nOverrides/pencilLayout';

export type MarkedAttribute = (typeof MARKED_ATTRIBUTES)[number];
export type TextSource = 'text' | MarkedAttribute;

/**
 * One editable spot on the page: an element's own marked text nodes (source `text`), or one marked attribute of an
 * element. Found by a DOM scan; measured separately on every frame that needs it.
 */
export interface MarkedSource {
  readonly id: string;
  readonly element: Element;
  readonly source: TextSource;
  /** The marked text nodes (source `text` only). */
  readonly nodes: ReadonlyArray<Text>;
  /** Registry indexes found here, deduplicated, in order of appearance. */
  readonly indexes: ReadonlyArray<number>;
}

export interface MeasuredSource extends MarkedSource {
  /** The visible part: inside the viewport and every clipping ancestor. */
  readonly rect: Rect;
  /** Position among the visible sources of the same element (text first, then attributes). */
  readonly slot: number;
}

// Stable ids per element, so React keeps a pencil's button (and its focus) across rescans.
const elementIds = new WeakMap<Element, number>();
const counter = { next: 0 };
const elementId = (el: Element): number => {
  const known = elementIds.get(el);
  if (known !== undefined) return known;
  counter.next += 1;
  elementIds.set(el, counter.next);
  return counter.next;
};

const SOURCE_ORDER: ReadonlyArray<TextSource> = ['text', ...MARKED_ATTRIBUTES];

/**
 * Every marked text in `root`: text nodes grouped by their parent element, and marked attributes one by one. Elements
 * for which `isExcluded` holds (the editor's own UI) are skipped. Document order, text before attributes per element.
 */
export const scanMarkedSources = (root: Element, isExcluded: (el: Element) => boolean): ReadonlyArray<MarkedSource> => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeValue?.includes(MARKER_START) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  const textNodes = A.unfold(walker.nextNode(), (n) =>
    n ? Option.some([n as Text, walker.nextNode()]) : Option.none(),
  );

  const textSources = pipe(
    textNodes,
    A.filterMap((node) =>
      pipe(
        Option.fromNullable(node.parentElement),
        Option.filter((parent) => !isExcluded(parent)),
        Option.map((parent) => ({ parent, node })),
      ),
    ),
    A.groupBy(({ parent }) => String(elementId(parent))),
    (groups) => Object.values(groups),
    A.map((group): MarkedSource => {
      const { parent } = group[0];
      return {
        id: `${elementId(parent)}:text`,
        element: parent,
        source: 'text',
        nodes: group.map((g) => g.node),
        indexes: pipe(
          group,
          A.flatMap((g) => markerIndexes(g.node.nodeValue ?? '')),
          A.dedupe,
        ),
      };
    }),
  );

  const selector = MARKED_ATTRIBUTES.map((a) => `[${a}*="${MARKER_START}"]`).join(',');
  const attributeSources = pipe(
    Array.from(root.querySelectorAll(selector)),
    A.filter((el) => !isExcluded(el)),
    A.flatMap((el) =>
      pipe(
        MARKED_ATTRIBUTES,
        A.filterMap((attribute) =>
          pipe(
            Option.fromNullable(el.getAttribute(attribute)),
            Option.map((value) => A.dedupe(markerIndexes(value))),
            Option.filter((indexes) => indexes.length > 0),
            Option.map(
              (indexes): MarkedSource => ({
                id: `${elementId(el)}:${attribute}`,
                element: el,
                source: attribute,
                nodes: [],
                indexes,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  return [...textSources, ...attributeSources].sort((a, b) => {
    if (a.element === b.element) return SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source);
    return a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
};

export interface Measure {
  /** Client rect of a text node's content (a Range over it). */
  readonly text: (node: Text) => Rect;
  readonly element: (el: Element) => Rect;
  /** Whether the element clips its content (overflow other than visible). */
  readonly clips: (el: Element) => boolean;
  /** Whether the source may show at all, given open modals (see `modalLayerFilter`). */
  readonly inTopLayer: (el: Element) => boolean;
  readonly viewport: Rect;
}

const clipByAncestors = (el: Element, rect: Rect, measure: Measure): Rect =>
  A.reduce(
    A.unfold(el.parentElement, (p) => (p && p !== document.body ? Option.some([p, p.parentElement]) : Option.none())),
    rect,
    (r, ancestor) => (measure.clips(ancestor) ? intersectRects(r, measure.element(ancestor)) : r),
  );

const rawRect = (source: MarkedSource, measure: Measure): Option.Option<Rect> =>
  source.source === 'text'
    ? pipe(
        source.nodes,
        A.map(measure.text),
        A.filter((r) => !isEmptyRect(r)),
        A.match({ onEmpty: () => Option.none(), onNonEmpty: (rs) => Option.some(A.reduce(rs, rs[0], unionRects)) }),
      )
    : pipe(
        Option.some(measure.element(source.element)),
        Option.filter((r) => !isEmptyRect(r)),
      );

/**
 * The sources that can be seen right now, with their visible rect: not empty, inside the viewport and every clipping
 * ancestor (grid cells, scrollers), and in the top layer when a modal is open. Slots number each element's visible
 * sources from 0.
 */
export const measureSources = (sources: ReadonlyArray<MarkedSource>, measure: Measure): ReadonlyArray<MeasuredSource> =>
  pipe(
    sources,
    A.filter((s) => s.element.isConnected && measure.inTopLayer(s.element)),
    A.filterMap((s) =>
      pipe(
        rawRect(s, measure),
        Option.map((r) => clipByAncestors(s.element, intersectRects(r, measure.viewport), measure)),
        Option.filter((r) => !isEmptyRect(r)),
        Option.map((rect) => ({ ...s, rect })),
      ),
    ),
    A.reduce({ out: [] as ReadonlyArray<MeasuredSource>, last: null as Element | null, slot: 0 }, (acc, s) => {
      const slot = s.element === acc.last ? acc.slot + 1 : 0;
      return { out: [...acc.out, { ...s, slot }], last: s.element, slot };
    }),
    (acc) => acc.out,
  );

/**
 * While MUI modals (dialogs, menus, popovers, drawers) are open, only the top one and the poppers opened after it
 * (tooltips, selects inside it) can be seen or used; texts behind the backdrop get no pencil. `isOwnUi` excludes the
 * editor's own dialog.
 */
export const modalLayerFilter = (doc: Document, isOwnUi: (el: Element) => boolean): ((el: Element) => boolean) => {
  const top = pipe(
    Array.from(doc.querySelectorAll('.MuiModal-root:not(.MuiModal-hidden)')),
    A.filter((m) => !isOwnUi(m)),
    A.last,
  );
  return Option.match(top, {
    onNone: () => () => true,
    onSome: (modal) => (el) => {
      if (modal.contains(el)) return true;
      const popper = el.closest('.MuiPopper-root, [role="tooltip"]');
      return popper !== null && (modal.compareDocumentPosition(popper) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
    },
  });
};

// Overflow styles rarely change; reading computed style for every ancestor on every frame would not be cheap.
const clipping = new WeakMap<Element, boolean>();

/** The browser's measurements for one frame (the modal layer is read once, at creation). */
export const browserMeasure = (isOwnUi: (el: Element) => boolean): Measure => {
  const range = document.createRange();
  return {
    text: (node) => {
      range.selectNodeContents(node);
      return range.getBoundingClientRect();
    },
    element: (el) => el.getBoundingClientRect(),
    clips: (el) => {
      const known = clipping.get(el);
      if (known !== undefined) return known;
      const style = getComputedStyle(el);
      const clips = style.overflowX !== 'visible' || style.overflowY !== 'visible';
      clipping.set(el, clips);
      return clips;
    },
    inTopLayer: modalLayerFilter(document, isOwnUi),
    viewport: { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight },
  };
};
